import { supabase } from '../lib/supabase';
import { getQuickPeriodUTCRange } from '../utils/dateRangeHelper';

// Convert database snake_case to React camelCase
const convertToSalarioObject = (dbRecord) => {
  if (!dbRecord) return null;

  // COALESCE: use loncheado's created_at as primary date, salario's as fallback
  const effectiveDate = dbRecord?.loncheados?.created_at || dbRecord?.created_at || null;

  return {
    id: dbRecord?.id,
    loncheadoId: dbRecord?.loncheado_id,
    empleadoId: dbRecord?.empleado_id,
    empleadoNombre: dbRecord?.empleados?.nombre || '-',
    clienteNombre: dbRecord?.loncheados?.clientes?.nombre || '-',
    productoNombre: dbRecord?.loncheados?.productos?.nombre || '-',
    tipo: dbRecord?.tipo,
    pesoLoncheado: parseFloat(dbRecord?.peso_loncheado) || 0,
    importe: parseFloat(dbRecord?.importe) || 0,
    salario: parseFloat(dbRecord?.importe) || 0,
    // Use effective date (loncheado date takes priority)
    createdAt: effectiveDate,
    created_at: effectiveDate,
    // Keep raw loncheado cliente_id for client-side filtering
    _loncheadoClienteId: dbRecord?.loncheados?.cliente_id,
  };
};

/**
 * Build UTC date bounds from service filters.
 * Returns { fromUTC, toUTCExclusive } — both may be null.
 *
 * Priority:
 *  1. filters.dateFromUTC / filters.dateToUTCExclusive (pre-computed by caller)
 *  2. filters.period quick range
 *  3. null (no date filter)
 */
const buildDateBounds = (filters = {}) => {
  // Pre-computed UTC strings passed by the page (custom mode)
  if (filters?.dateFromUTC || filters?.dateToUTCExclusive) {
    return {
      fromUTC: filters?.dateFromUTC || null,
      toUTCExclusive: filters?.dateToUTCExclusive || null,
    };
  }

  // Quick period
  if (filters?.period && filters?.period !== 'all' && filters?.period !== 'custom') {
    const range = getQuickPeriodUTCRange(filters?.period);
    if (range) return { fromUTC: range?.fromUTC, toUTCExclusive: range?.toUTCExclusive };
  }

  return { fromUTC: null, toUTCExclusive: null };
};

export const salariosService = {
  /**
   * Get salarios joined with loncheados.
   *
   * Date filtering uses COALESCE(loncheados.created_at, salarios.created_at):
   *  - We fetch loncheados.created_at in the JOIN
   *  - Apply date bounds client-side using the effective date
   *  - This guarantees a salary record appears in the same period as its loncheado
   *
   * All other filters (empleado, cliente, tipo) are applied server-side via AND.
   */
  async getLoncheadosWithSalaries(filters = {}) {
    const { fromUTC, toUTCExclusive } = buildDateBounds(filters);

    let query = supabase
      ?.from('salarios')
      ?.select(`
        id,
        loncheado_id,
        empleado_id,
        tipo,
        peso_loncheado,
        importe,
        created_at,
        empleados!salarios_empleado_id_fkey(
          nombre
        ),
        loncheados!salarios_loncheado_id_fkey(
          id,
          created_at,
          cliente_id,
          producto_id,
          clientes(
            nombre
          ),
          productos(
            nombre
          )
        )
      `)
      ?.order('created_at', { ascending: false });

    // Apply employee filter server-side
    if (filters?.empleadoId && filters?.empleadoId !== 'all') {
      query = query?.eq('empleado_id', filters?.empleadoId);
    }

    const { data, error } = await query;
    if (error) throw error;

    // Transform to camelCase (effective date = loncheado.created_at COALESCE salario.created_at)
    let transformed = (data || [])?.map(convertToSalarioObject);

    // ── Date filtering client-side using effective date ──────────────────────
    // This implements COALESCE(loncheados.created_at, salarios.created_at) correctly
    if (fromUTC || toUTCExclusive) {
      transformed = transformed?.filter(item => {
        const effectiveMs = item?.createdAt ? new Date(item.createdAt)?.getTime() : null;
        if (effectiveMs === null) return false;
        if (fromUTC && effectiveMs < new Date(fromUTC)?.getTime()) return false;
        if (toUTCExclusive && effectiveMs >= new Date(toUTCExclusive)?.getTime()) return false;
        return true;
      });
    }

    // ── Cliente filter client-side (join field) ──────────────────────────────
    if (filters?.clienteId && filters?.clienteId !== 'all') {
      transformed = transformed?.filter(item =>
        String(item?._loncheadoClienteId) === String(filters?.clienteId)
      );
    }

    return transformed;
  },

  // Get employee summary using aggregations
  async getEmployeeSummary(filters = {}) {
    const loncheados = await this.getLoncheadosWithSalaries(filters);

    const employeeMap = {};
    loncheados?.forEach(record => {
      const empId = record?.empleadoId;
      if (!employeeMap?.[empId]) {
        employeeMap[empId] = {
          empleadoId: empId,
          empleadoNombre: record?.empleadoNombre || '-',
          totalLoncheados: 0,
          totalSalario: 0,
        };
      }
      employeeMap[empId].totalLoncheados++;
      employeeMap[empId].totalSalario += parseFloat(record?.importe) || 0;
    });

    return Object.values(employeeMap)?.map(emp => ({
        ...emp,
        promedioSalarioPorLoncheado: emp?.totalLoncheados > 0
          ? emp?.totalSalario / emp?.totalLoncheados
          : 0,
      }))?.sort((a, b) => b?.totalSalario - a?.totalSalario);
  },

  // Get overall statistics
  async getOverallStats(filters = {}) {
    const loncheados = await this.getLoncheadosWithSalaries(filters);

    const totalLoncheados = loncheados?.length || 0;
    const totalSalario = loncheados?.reduce((sum, item) => sum + (parseFloat(item?.importe) || 0), 0);
    const promedioSalario = totalLoncheados > 0 ? totalSalario / totalLoncheados : 0;

    return { totalLoncheados, totalSalario, promedioSalario };
  },

  // Get evolution data for charts
  async getEvolutionData(filters = {}, groupBy = 'day') {
    const loncheados = await this.getLoncheadosWithSalaries(filters);

    const grouped = {};
    loncheados?.forEach(item => {
      const date = new Date(item?.createdAt);
      let key;
      if (groupBy === 'day') {
        key = `${date?.getDate()}/${date?.getMonth() + 1}`;
      } else if (groupBy === 'week') {
        const weekStart = new Date(date);
        weekStart?.setDate(date?.getDate() - date?.getDay());
        key = `Sem ${weekStart?.getDate()}/${weekStart?.getMonth() + 1}`;
      } else if (groupBy === 'month') {
        key = `${date?.getMonth() + 1}/${date?.getFullYear()}`;
      }
      if (!grouped?.[key]) {
        grouped[key] = { name: key, salario: 0 };
      }
      grouped[key].salario += item?.importe;
    });

    return Object.values(grouped);
  },

  // Check if current user is admin
  async isCurrentUserAdmin() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return false;
    const { data, error } = await supabase
      ?.from('empleados')
      ?.select('rol')
      ?.eq('user_id', user?.id)
      ?.single();
    if (error) return false;
    return data?.rol === 'admin';
  }
};